import { getServiceUrl } from './api.config';
import { ApiClientOptions } from './types/api';
import ApiClient from './utils/ApiClient';
import checkNot from './utils/checkNot';
import {
  isNotEmptyString,
  isPositiveInteger,
  isTrue,
} from './utils/validators';

export default class GdsnApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('gdsn'), options);
  }

  public Ping() {
    const query = '/gdsn/v2/platform/ping';
    const parameters = '?alk=1';
    return this.get(query, parameters);
  }

  public GdsnRefreshStatus(entityId: number) {
    const query = '/gdsn/v1/subscription/status';
    return this.post(query, { subscription_request_id: entityId });
  }

  public SendCIHW(cisyncID: number, deletionReasonCode: string) {
    const query = '/gdsn/v2/cihw/send';
    return this.post(query, {
      data: { cisync_id: cisyncID, deletionReasonCode },
    });
  }

  public Whois(reference: string, type: string) {
    const query = '/gdsn/v1/gtin/whois';
    return this.post(query, { reference, type });
  }

  public GdsnCreate(subscription: any) {
    const query = '/gdsn/v1/subscription/create';
    const payload = {
      environment: subscription.environment,
      gln_recipient: subscription.glnRecipient,
      gln_source: subscription.glnSource,
      gpc: subscription.gpc,
    };
    return this.post(query, payload);
  }

  public GdsnGLNList(filters: any = {}, offset?: number, limit?: number) {
    const query = '/gdsn/v1/gln';
    let parameters = '?alk=1';

    // Build query
    if (checkNot(filters.filter_gln, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_gln=${filters.filter_gln}`;
    }
    if (checkNot(filters.filter_gln_in, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_gln_in=${filters.filter_gln_in.join()}`;
    }
    if (checkNot(filters.filter_ean, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_ean=${filters.filter_ean}`;
    }
    if (checkNot(filters.filter_name, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_name=${filters.filter_name}`;
    }
    if (checkNot(filters.filter_type, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_type=${filters.filter_type}`;
    }
    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${offset}`;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${limit}`;
    }
    return this.get(query, parameters);
  }

  // GDSN 3.1

  public GdsnCatalogueItemSynchronisationListReport(
    filters: any = {},
    search: any = {},
    offset?: number,
    limit?: number,
  ) {
    const query = '/gdsn/v2/catalogueitemsynchronisation';
    let parameters = '?alk=1&with_documents=true';

    if (checkNot(filters.organization, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_org_in=${filters.organization.map(
        (o: any) => o.value,
      )}`;
    }
    if (checkNot(search.sourceglns, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_source_gln_in=${search.sourceglns}`;
    }
    if (checkNot(search.targetglns, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_target_gln_in=${search.targetglns}`;
    }
    if (checkNot(search.gtins, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_gtin_in=${search.gtins}`;
    }
    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${offset}`;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${limit}`;
    }
    return this.get(query, parameters);
  }

  public GdsnCatalogueItemSynchronisationFilters() {
    const query = '/gdsn/v2/catalogueitemsynchronisation/filters';
    return this.get(query);
  }

  public GdsnCatalogueItemSynchronisationReport(csyncId: number) {
    const query = `/gdsn/v2/catalogueitemsynchronisation/${csyncId}`;
    const parameters = '?alk=1';
    return this.get(query, parameters);
  }

  public GdsnCatalogueItemSubscriptionListReport(
    queries: any = {},
    filters: any = {},
    offset?: number,
    limit?: number,
  ) {
    const query = '/gdsn/v2/catalogueitemsubscriptions';
    let parameters = '?alk=1';

    if (isNotEmptyString(filters.gln_recipient)) {
      parameters += `&filter_glnrecipient=${filters.gln_recipient}`;
    }

    if (isNotEmptyString(filters.gln_source)) {
      parameters += `&filter_glnsource=${filters.gln_source}`;
    }

    if (isTrue(queries.with_documents)) {
      parameters += '&with_documents=1';
    }

    if (isPositiveInteger(offset)) {
      parameters += `&offset=${offset}`;
    }
    if (isPositiveInteger(limit)) {
      parameters += `&limit=${limit}`;
    }
    return this.get(query, parameters);
  }

  public GdsnCatalogueItemSubscriptionCreate(subscription: any) {
    const query = '/gdsn/v2/catalogueitemsubscriptions';
    const payload = {
      data: {
        dataRecipient: subscription.glnRecipient, // Recipient
        gln: subscription.glnSource, // Source
        targetMarketCountryCode: subscription.targetMarketCountryCode,
      },
      validate: true,
    };
    return this.post(query, payload);
  }

  public GdsnCatalogueItemSubscriptionDelete(unsubscription: any) {
    const query = '/gdsn/v2/catalogueitemsubscriptions';
    const payload = {
      data: {
        dataRecipient: unsubscription.glnRecipient, // Recipient
        documentCommand_type: 'DELETE',
        gln: unsubscription.glnSource, // Source
        targetMarketCountryCode: unsubscription.targetMarketCountryCode,
      },
      validate: true,
    };
    return this.post(query, payload);
  }

  public GdsnCatalogueItemSubscriptionReport(cisId: number) {
    const query = `/gdsn/v2/catalogueitemsubscriptions/${cisId}`;
    const parameters = '?alk=1';
    return this.get(query, parameters);
  }

  public DownloadDocument(id: number | string) {
    const query = `/gdsn/v2/message/${id}`;
    const parameters = `?alk=1`;
    return this.getBlob(query, parameters);
  }

  public ListRCI(filters: any, offset?: number, limit?: number) {
    const query = '/gdsn/v2/rci/list';
    let parameters = '?alk=1';

    if (isNotEmptyString(filters.filter_gln)) {
      parameters += `&filter_gln=${filters.filter_gln}`;
    }
    if (isNotEmptyString(filters.filter_gtin)) {
      parameters += `&filter_gtin=${filters.filter_gtin}`;
    }
    if (isPositiveInteger(offset)) {
      parameters += `&offset=${offset}`;
    }
    if (isPositiveInteger(limit)) {
      parameters += `&limit=${limit}`;
    }
    return this.get(query, parameters);
  }

  public ListParty(filters: any, offset?: number, limit?: number) {
    const query = '/gdsn/v2/party/list';
    let parameters = '?alk=1';

    if (isNotEmptyString(filters.filter_gln)) {
      parameters += `&filter_gln=${filters.filter_gln}`;
    }
    if (isNotEmptyString(filters.filter_datapool_gln)) {
      parameters += `&filter_datapool_gln=${filters.filter_datapool_gln}`;
    }
    if (isPositiveInteger(offset)) {
      parameters += `&offset=${offset}`;
    }
    if (isPositiveInteger(limit)) {
      parameters += `&limit=${limit}`;
    }
    return this.get(query, parameters);
  }

  public CreateCIS(cis: any) {
    const query = '/gdsn/v2/catalogueitemsubscriptions';
    return this.post(query, cis);
  }

  public DeleteCIS(cisId: number) {
    const query = `/gdsn/v2/catalogueitemsubscriptions/remove/${cisId}`;
    const parameters = `?alk=1`;
    return this.get(query, parameters);
  }

  public CreateParty(party: any) {
    const query = '/gdsn/v2/party/register';
    return this.post(query, party);
  }

  public DeleteParty(party: any) {
    const query = '/gdsn/v2/party/delete';
    return this.post(query, party);
  }

  public RegisterRCI(rci: any) {
    const query = '/gdsn/v2/rci/register';
    return this.post(query, rci);
  }

  public CorrectRCI(rci: any) {
    const query = '/gdsn/v2/rci/correct';
    return this.put(query, rci);
  }

  // Price: Relation
  public ListRelation(filters: any) {
    const query = '/gdsn/v2/psr/list';
    let parameters = '?alk=1';

    if (isNotEmptyString(filters.filter_gln)) {
      parameters += `&gln=${filters.filter_gln}`;
    }
    if (isPositiveInteger(filters.offset)) {
      parameters += `&offset=${filters.offset}`;
    }
    if (isPositiveInteger(filters.limit)) {
      parameters += `&limit=${filters.limit}`;
    }
    return this.get(query, parameters);
  }

  public DeleteRelation(id: number) {
    const query = `/gdsn/v2/psr/relation/${id}`;
    const parameters = '?alk=1';
    return this.delete(query, parameters);
  }

  // Price: Items
  public ListPrice(filters: any) {
    const query = '/gdsn/v2/psritems/list';
    let parameters = '?alk=1';

    if (isNotEmptyString(filters.filter_gln)) {
      parameters += `&filter_gln=${filters.filter_gln}`;
    }
    if (isNotEmptyString(filters.filter_gtins_in)) {
      parameters += `&filter_gtins_in=${filters.filter_gtins_in}`;
    }
    if (isPositiveInteger(filters.offset)) {
      parameters += `&offset=${filters.offset}`;
    }
    if (isPositiveInteger(filters.limit)) {
      parameters += `&limit=${filters.limit}`;
    }
    return this.get(query, parameters);
  }

  public DetailPrice(psrID: number, gtin?: string) {
    const query = `/gdsn/v2/psritems/${psrID}`;
    let parameters = '?alk=1';

    if (isNotEmptyString(gtin)) {
      parameters += `&gtin=${gtin}`;
    }
    return this.get(query, parameters);
  }

  public RemoveItem(psrID: number) {
    const query = `/gdsn/v2/psritem/${psrID}`;
    const parameters = '?alk=1';
    return this.delete(query, parameters);
  }

  // Price: PCR
  public SendPCR(pcr: any) {
    const query = '/gdsn/v2/pcr/send';
    return this.post(query, pcr);
  }

  // File
  public UploadGEPIRCrawlCSV(file: any) {
    const query = '/gdsn/v1/gepir/crawl';
    return this.post(query, {}, file);
  }

  public GdsnPushAS2(payload: any, file: any) {
    const query = '/gdsn/v2/as2/push';
    return this.post(query, payload, file);
  }

  public CISAutoSubscribe(sourceGLN: string, commandType: string) {
    const query = '/gdsn/v2/cis/autosubscribe';
    const payload = {
      source_gln: sourceGLN,
      documentCommand_type: commandType,
    };
    return this.post(query, payload);
  }

  public GLNUpdate(id: number, nameLegal: string) {
    const query = `/gdsn/v1/gln/${id}`;
    const payload = {
      nameLegal,
    };
    return this.post(query, payload);
  }

  public CINList(parameters: string) {
    const query = '/gdsn/v3/cin/list';
    return this.get(query, parameters);
  }

  public CINDocumentDetail(id: number) {
    const query = `/gdsn/v3/cin/${id}/documents`;
    const parameters = '?alk=1';
    return this.get(query, parameters);
  }
}
